//// FILE

import ASSETS from '@generio-ai/library-legacy/constants/assets'
import MIMES from '@generio-ai/library-legacy/constants/mimes'

const EXIF_ORIENTATION_TO_ROTATE = Object.freeze({
    3: 180,
    5: 90,
    6: 90,
    7: 270,
    8: 270
})


// get extension from filename
export function extractExtension(filename) {
    // split the filename into parts on the dot
    const parts = filename.split('.')

    // if only one part, nothing to extract
    if (parts.length == 1) return null

    // pop last item and make it lower case
    return parts.pop().toLowerCase()
}

// remove extension from filename string
export function removeExtension(filename) {
    // split the filename into parts on the dot
    const parts = filename.split('.')

    // if there's no dot or only one part, return the filename as is
    if (parts.length === 1 || (parts[0] === '' && parts.length === 2)) {
        return filename
    }

    // join the parts back together, excluding the last part (the extension)
    return parts.slice(0, -1).join('.')
}

export function base64ToBinary(data) {
    // extract raw base64 string (in case of pre mime type)
    const base64 = data.split(',')[1]

    // convert base64 to binary
    const binary = atob(base64)

    return binary
}

export function base64ToArrayBuffer(data) {
    // create binary
    const binary = base64ToBinary(data)

    // create new array buffer
    const arrayBuffer = new ArrayBuffer(binary.length)

    // create unsigned int (8 bit) array
    const uint8Array = new Uint8Array(arrayBuffer)

    // iterate over all character
    for (let i = 0; i < binary.length; i++) {
        uint8Array[i] = binary.charCodeAt(i)
    }

    return arrayBuffer
}

export function base64ToUrl(data) {
    // ignore incorrect data
    if (data === null || data === undefined)
        return null

    // check if this.depth is a base64 string with a MIME type
    const base64Pattern = /^data:(.*);base64,(.*)$/
    const match = data.match(base64Pattern)

    if (match) {
        // extracted MIME type (e.g., 'image/png', 'image/jpeg')
        const mimeType = match[1]
        // base64 data    
        const base64Data = match[2]

        // decode base64 data and create a Blob
        const byteCharacters = atob(base64Data)
        const byteNumbers = Array.from(byteCharacters, char => char.charCodeAt(0))
        const byteArray = new Uint8Array(byteNumbers)
        const blob = new Blob([byteArray], { type: mimeType })

        // create a URL for the Blob
        const url = URL.createObjectURL(blob)

        return url
    }
    else {
        throw new Error("MIME type expected but not present.")
    }
}

// download file
export function downloadFile(data, type, name, extension) {
    // create download link
    const blob = new Blob([data], { type })
    const link = document.createElement('a')
    link.href = URL.createObjectURL(blob)
    link.download = `generio_${name}.${extension}`

    // download
    link.click()

    // clean up
    URL.revokeObjectURL(link.href)
}

export function getBase64FromImageUrl(url) {
    return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest()
        xhr.onload = function () {
            const reader = new FileReader()
            reader.onloadend = function () {
                resolve(reader.result)
            };
            reader.onerror = reject
            reader.readAsDataURL(xhr.response)
        };
        xhr.onerror = reject
        xhr.open('GET', url)
        xhr.responseType = 'blob'
        xhr.send()
    })
}

export async function readFiles(files, type) {
    const { fileData } = await readFilesWithParameters(files, type)
    return fileData
}

export async function readFilesWithParameters(files, type) {
    const entries = await Promise.all(
        [...files].map(async file => {
            const mimeType = resolveMimeType(file)
            validateMimeType(mimeType, type)

            const [rawData, parameters] = await Promise.all([
                readFileAsDataUrl(file),
                resolveFileParameters(file, type, mimeType)
            ])

            return {
                data: applyMimeTypeToData(mimeType, rawData),
                parameters
            }
        })
    )

    return {
        fileData: entries.map(entry => entry.data),
        fileParameters: entries.map(entry => entry.parameters)
    }
}

function readFileAsDataUrl(file) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = event => resolve(event.target.result)
        reader.onerror = error => reject(error)
        reader.readAsDataURL(file)
    })
}

async function resolveFileParameters(file, type, mimeType) {
    if (type !== ASSETS.TYPES.IMAGE) return null

    const rotate = await resolveImageRotation(file, mimeType)
    if (!rotate) return null

    return { rotate }
}

async function resolveImageRotation(file, mimeType) {
    if (!file) return 0

    const extension = extractExtension(file.name || '')
    const isJpeg = mimeType === 'image/jpeg' || mimeType === 'image/jpg' || extension === 'jpg' || extension === 'jpeg'
    const isTiff = mimeType === 'image/tiff' || extension === 'tif' || extension === 'tiff'

    if (!isJpeg && !isTiff) return 0

    const buffer = await readFileSliceAsArrayBuffer(file)
    const orientation = isTiff ?
        extractOrientationFromTiff(buffer) :
        extractOrientationFromJpeg(buffer)

    return EXIF_ORIENTATION_TO_ROTATE[orientation] || 0
}

async function readFileSliceAsArrayBuffer(file, size = 262144) {
    const slice = typeof file.slice === 'function' ? file.slice(0, size) : file
    if (typeof slice.arrayBuffer === 'function') {
        return slice.arrayBuffer()
    }

    return new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = event => resolve(event.target.result)
        reader.onerror = error => reject(error)
        reader.readAsArrayBuffer(slice)
    })
}

function extractOrientationFromJpeg(buffer) {
    const view = new DataView(buffer)
    if (view.byteLength < 4 || view.getUint16(0, false) !== 0xFFD8) return null

    let offset = 2

    while (offset + 4 <= view.byteLength) {
        if (view.getUint8(offset) !== 0xFF) return null

        const marker = view.getUint8(offset + 1)
        offset += 2

        if (marker === 0xD8 || marker === 0xD9) continue
        if (offset + 2 > view.byteLength) return null

        const size = view.getUint16(offset, false)
        if (size < 2 || offset + size > view.byteLength) return null

        if (marker === 0xE1 && size >= 8) {
            const exifOffset = offset + 2
            if (getAsciiString(view, exifOffset, 4) === 'Exif' && view.getUint16(exifOffset + 4, false) === 0x0000) {
                return extractOrientationFromTiffView(view, exifOffset + 6)
            }
        }

        offset += size
    }

    return null
}

function extractOrientationFromTiff(buffer) {
    return extractOrientationFromTiffView(new DataView(buffer), 0)
}

function extractOrientationFromTiffView(view, tiffOffset) {
    if (tiffOffset + 8 > view.byteLength) return null

    const byteOrder = view.getUint16(tiffOffset, false)
    const littleEndian = byteOrder === 0x4949 ? true : byteOrder === 0x4D4D ? false : null
    if (littleEndian === null) return null

    if (view.getUint16(tiffOffset + 2, littleEndian) !== 0x002A) return null

    const firstIfdOffset = view.getUint32(tiffOffset + 4, littleEndian)
    return extractOrientationFromIfd(view, tiffOffset, tiffOffset + firstIfdOffset, littleEndian)
}

function extractOrientationFromIfd(view, tiffOffset, ifdOffset, littleEndian) {
    if (ifdOffset + 2 > view.byteLength) return null

    const entryCount = view.getUint16(ifdOffset, littleEndian)

    for (let i = 0; i < entryCount; i++) {
        const entryOffset = ifdOffset + 2 + (i * 12)
        if (entryOffset + 12 > view.byteLength) return null

        const tag = view.getUint16(entryOffset, littleEndian)
        if (tag !== 0x0112) continue

        const type = view.getUint16(entryOffset + 2, littleEndian)
        const count = view.getUint32(entryOffset + 4, littleEndian)

        if (type === 3 && count === 1) {
            return view.getUint16(entryOffset + 8, littleEndian)
        }

        const valueOffset = view.getUint32(entryOffset + 8, littleEndian)
        const absoluteOffset = tiffOffset + valueOffset

        if (type === 3 && count > 0 && absoluteOffset + 2 <= view.byteLength) {
            return view.getUint16(absoluteOffset, littleEndian)
        }

        if (type === 4 && count > 0 && absoluteOffset + 4 <= view.byteLength) {
            return view.getUint32(absoluteOffset, littleEndian)
        }

        return null
    }

    return null
}

function getAsciiString(view, offset, length) {
    let output = ''

    for (let i = 0; i < length; i++) {
        output += String.fromCharCode(view.getUint8(offset + i))
    }

    return output
}

export function resolveMimeType(file) {
    if (!file) return null
    const currentType = typeof file.type === 'string' ? file.type.toLowerCase() : file.type
    const extensionType = resolveMimeTypeFromExtension(file.name)
    if (extensionType) {
        return extensionType
    }
    if (currentType && currentType !== 'application/octet-stream') {
        return currentType
    }
    return extensionType || currentType || null
}
export function resolveMimeTypeFromExtension(name) {
    if (!name || typeof name !== 'string') return null
    let ext = name.split('.').pop()?.toLowerCase()
    const map = Object.assign({}, ...Object.values(MIMES))
    return map[ext] || null
}

export function supportMimeTypesByType(type) {
    if (type === ASSETS.TYPES.TEXT) {
        return Object.values(MIMES.TEXT)
    }
    if (type === ASSETS.TYPES.IMAGE) {
        return Object.values(MIMES.IMAGE)
    }
    if (type === ASSETS.TYPES.MODEL) {
        return Object.values(MIMES.MODEL)
    }
    return []
}

export function validateMimeType(mime, type) {
    const validMimes = supportMimeTypesByType(type)
    const normalizedMime = typeof mime === 'string' ? mime.toLowerCase() : mime
    if (!validMimes.includes(normalizedMime)) {
        throw new Error(`Unsupported mime type: ${mime}`)
    }
}

export function applyMimeTypeToData(mime, data) {
    if (!mime || typeof data !== 'string') return data
    return data.replace(/^data:[^;]+/, `data:${mime}`)
}
